import React, { createContext, useContext, useMemo, type ReactNode } from 'react';

import { type SizeType, useSize, SizeContextProvider } from '../../features/config';
import { useOverflow } from '../Overflow/useOverflow';

import type { ButtonProps } from '../Button';

export type BreadcrumbItem = {
    id: string;
    label: string;
    href?: string;
    onClick?: ButtonProps['onClick'];
};

export type BreadcrumbConfigProps = {
    items: BreadcrumbItem[];
    size?: SizeType;
    visibleItemsAmount?: number;
    minVisibleItemsAmount?: number;
};

export const useBreadcrumb = ({
    items,
    size,
    visibleItemsAmount,
    minVisibleItemsAmount = 1
}: BreadcrumbConfigProps) => {
    const mergedSize = useSize(size, 'small', 'Breadcrumb');
    const overflow = useOverflow(items, {
        visibleItemsAmount,
        mode: 'end',
        minVisibleItemsAmount
    });
    const contextValue = useMemo(
        () => ({
            overflow,
            items: overflow.items.map(({ data }) => data),
            hiddenItems: overflow.hiddenItems.map(({ data }) => data),
            size: mergedSize
        }),
        [overflow, mergedSize]
    );
    return contextValue;
};

export type BreadcrumbContextType = ReturnType<typeof useBreadcrumb>;

const BreadcrumbContext = createContext<BreadcrumbContextType | null>(null);

export const useBreadcrumbContext = () => {
    const context = useContext(BreadcrumbContext);
    if (!context) {
        throw new Error(
            'Breadcrumb compound components cannot be rendered outside the Breadcrumb component'
        );
    }
    return context;
};

export const BreadcrumbProvider = ({
    value,
    children
}: {
    value: BreadcrumbContextType;
    children: ReactNode;
}) => {
    return (
        <SizeContextProvider size={value.size}>
            <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
        </SizeContextProvider>
    );
};
